import type { ContinentKey, Country, PoolKey } from '../types.js';

export const EASY_ISO2: readonly string[] = [
  // Asia
  'KR', 'JP', 'CN', 'IN', 'TH', 'VN', 'ID', 'PH', 'SA', 'TR',
  // Europe
  'GB', 'FR', 'DE', 'IT', 'ES', 'RU', 'GR', 'NL', 'SE', 'NO',
  // Americas
  'US', 'CA', 'MX', 'BR', 'AR',
  // Africa
  'EG', 'ZA', 'KE',
  // Oceania
  'AU', 'NZ',
];

// 동·동남·남아시아 — 한국 학습자에게 친숙한 권역
export const EAST_SOUTH_ASIA_ISO2: readonly string[] = [
  'CN', 'JP', 'KP', 'KR', 'MN', 'TW',
  'BN', 'ID', 'KH', 'LA', 'MM', 'MY', 'PH', 'SG', 'TH', 'TL', 'VN',
  'BD', 'BT', 'IN', 'LK', 'MV', 'NP', 'PK',
];

// 서·중앙아시아 — 스탄국·중동·코카서스. 아프가니스탄은 사용자 의도(스탄국 묶음)에 맞춰 여기로.
export const WEST_CENTRAL_ASIA_ISO2: readonly string[] = [
  'KZ', 'KG', 'TJ', 'TM', 'UZ', 'AF',
  'AE', 'AM', 'AZ', 'BH', 'CY', 'GE', 'IL', 'IQ', 'IR', 'JO',
  'KW', 'LB', 'OM', 'PS', 'QA', 'SA', 'SY', 'TR', 'YE',
];

export interface PoolPreview {
  total: number;
  samples: string[];
}

/**
 * 풀 선택 시 보여줄 미리보기 — 총 N개국과 랜덤 샘플 4~5개 이름.
 * 'review' 풀은 학습 통계 기반이라 미리보기가 의미 없어 null.
 */
export function getPoolPreview(
  pool: PoolKey,
  all: readonly Country[],
  sampleSize = 5,
  rng: () => number = Math.random,
): PoolPreview | null {
  if (pool === 'review') return null;
  const filtered = filterPool(all as Country[], pool);
  const samples = shuffle(filtered, rng)
    .slice(0, Math.min(sampleSize, filtered.length))
    .map((c) => c.nameKo);
  return { total: filtered.length, samples };
}

export function filterPool(all: Country[], pool: PoolKey): Country[] {
  if (pool === 'easy') {
    const set = new Set(EASY_ISO2);
    return all.filter((c) => set.has(c.iso2));
  }
  if (pool === 'review') {
    // 복습 모드는 전체 국가가 후보군. 실제 가중치 픽/필터는 GlobeQuiz.buildQuestion에서.
    return all.slice();
  }
  if (pool === 'East-South Asia') {
    const set = new Set(EAST_SOUTH_ASIA_ISO2);
    return all.filter((c) => set.has(c.iso2));
  }
  if (pool === 'West-Central Asia') {
    const set = new Set(WEST_CENTRAL_ASIA_ISO2);
    return all.filter((c) => set.has(c.iso2));
  }
  return all.filter((c) => c.continent === (pool as ContinentKey));
}

export function pickWeighted<T>(
  candidates: readonly T[],
  weight: (item: T) => number,
  rng: () => number = Math.random,
): T | null {
  if (candidates.length === 0) return null;
  let total = 0;
  const weights: number[] = [];
  for (const c of candidates) {
    const w = Math.max(0, weight(c));
    weights.push(w);
    total += w;
  }
  if (total <= 0) return null;
  let r = rng() * total;
  for (let i = 0; i < candidates.length; i++) {
    r -= weights[i]!;
    if (r <= 0) return candidates[i]!;
  }
  return candidates[candidates.length - 1]!;
}

export function pickRandom<T>(arr: readonly T[], rng: () => number = Math.random): T {
  if (arr.length === 0) {
    throw new Error('pickRandom called on empty array');
  }
  const idx = Math.floor(rng() * arr.length);
  return arr[idx] ?? arr[0]!;
}

export function shuffle<T>(arr: readonly T[], rng: () => number = Math.random): T[] {
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    const a = copy[i]!;
    const b = copy[j]!;
    copy[i] = b;
    copy[j] = a;
  }
  return copy;
}
